// src/features/health/components/medications/MedicationDetails.tsx
// Full medication details view component

import React from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Pill,
  Clock,
  Calendar,
  User,
  Building,
  FileText,
  AlertTriangle,
  Bell,
  BellOff,
  Activity,
  Package,
  Edit,
  Trash2,
  Power,
  ArrowLeft,
  TrendingUp,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/core/lib/utils';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Separator } from '@/components/ui/Separator';
import { Progress } from '@/components/ui/Progress';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/Tooltip';
import { useMedication, useDoseHistory } from '../hooks/useMedications';
import { useMedicationAdherenceCalculation } from '../hooks/useMedications';
import {
  frequencyLabels,
  routeLabels,
  MedicationFrequency,
  MedicationRoute,
  adherenceStatusLabels,
  adherenceStatusColors,
} from '../types/medicationTypes';
import { format as formatDate, subDays } from 'date-fns';

// ============================================================================
// Types
// ============================================================================

interface MedicationDetailsProps {
  medicationId: string;
  childId: string;
  onEdit?: () => void;
  onDelete?: () => void;
  onBack?: () => void;
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export const MedicationDetails: React.FC<MedicationDetailsProps> = ({
  medicationId,
  childId,
  onEdit,
  onDelete,
  onBack,
  className,
}) => {
  const { data: medication, isLoading, error } = useMedication(medicationId);

  // Get adherence stats for last 30 days
  const today = new Date();
  const thirtyDaysAgo = subDays(today, 30);
  const { data: adherenceStats } = useMedicationAdherenceCalculation(
    medicationId,
    formatDate(thirtyDaysAgo, 'yyyy-MM-dd'),
    formatDate(today, 'yyyy-MM-dd')
  );

  // Get recent dose history
  const { data: doseHistory } = useDoseHistory(medicationId, { limit: 10 });

  if (isLoading) {
    return <MedicationDetailsSkeleton />;
  }

  if (error || !medication) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-navy mb-2">
          Medication Not Found
        </h3>
        <p className="text-warm-gray mb-4">
          This medication may have been deleted or you don't have access.
        </p>
        <Button onClick={onBack}>Go Back</Button>
      </div>
    );
  }

  const isLowStock =
    medication.currentQuantity !== undefined &&
    medication.refillQuantity !== undefined &&
    medication.currentQuantity <= medication.refillQuantity * 0.2;

  const isRefillDue =
    medication.refillDate && new Date(medication.refillDate) <= new Date();

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack} className="mt-1">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          )}
          <div>
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  'w-12 h-12 rounded-xl flex items-center justify-center',
                  medication.isActive
                    ? 'bg-sage-100 text-sage-600'
                    : 'bg-gray-100 text-gray-400'
                )}
              >
                <Pill className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-2xl font-display font-semibold text-navy">
                  {medication.name}
                </h1>
                {medication.genericName && (
                  <p className="text-warm-gray">({medication.genericName})</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onEdit} className="gap-2">
            <Edit className="w-4 h-4" />
            Edit
          </Button>
          <Button
            variant="ghost"
            onClick={onDelete}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Status Badges */}
      <div className="flex flex-wrap gap-2">
        <Badge
          variant={medication.isActive ? 'success' : 'secondary'}
          className="text-sm"
        >
          {medication.isActive ? 'Active' : 'Inactive'}
        </Badge>
        {medication.reminderEnabled && (
          <Badge variant="outline" className="gap-1 text-sm">
            <Bell className="w-3 h-3" />
            Reminders Enabled
          </Badge>
        )}
        {isLowStock && (
          <Badge variant="warning" className="gap-1 text-sm">
            <AlertTriangle className="w-3 h-3" />
            Low Stock
          </Badge>
        )}
        {isRefillDue && (
          <Badge variant="error" className="gap-1 text-sm">
            <Calendar className="w-3 h-3" />
            Refill Due
          </Badge>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Dosage Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-sage-600" />
                Dosage Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-warm-gray mb-1">Dosage</p>
                  <p className="text-lg font-semibold text-navy">
                    {medication.dosage}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-warm-gray mb-1">Frequency</p>
                  <p className="text-lg font-semibold text-navy">
                    {frequencyLabels[medication.frequency as MedicationFrequency]}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-warm-gray mb-1">Route</p>
                  <p className="text-lg font-semibold text-navy">
                    {routeLabels[medication.route as MedicationRoute]}
                  </p>
                </div>
              </div>

              {medication.reminderTimes.length > 0 && (
                <>
                  <Separator className="my-4" />
                  <div>
                    <p className="text-sm text-warm-gray mb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Scheduled Times
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {medication.reminderTimes.map((time, index) => {
                        const [hours, minutes] = time.split(':');
                        const date = new Date();
                        date.setHours(parseInt(hours), parseInt(minutes));
                        return (
                          <Badge key={index} variant="secondary">
                            {format(date, 'h:mm a')}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Prescription Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-sage-600" />
                Prescription Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                {medication.prescribingDoctor && (
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-warm-gray mt-0.5" />
                    <div>
                      <p className="text-sm text-warm-gray">Prescribing Doctor</p>
                      <p className="font-medium text-navy">
                        {medication.prescribingDoctor}
                      </p>
                    </div>
                  </div>
                )}
                {medication.pharmacy && (
                  <div className="flex items-start gap-3">
                    <Building className="w-5 h-5 text-warm-gray mt-0.5" />
                    <div>
                      <p className="text-sm text-warm-gray">Pharmacy</p>
                      <p className="font-medium text-navy">{medication.pharmacy}</p>
                    </div>
                  </div>
                )}
                {medication.rxNumber && (
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-warm-gray mt-0.5" />
                    <div>
                      <p className="text-sm text-warm-gray">Rx Number</p>
                      <p className="font-medium text-navy font-mono">
                        {medication.rxNumber}
                      </p>
                    </div>
                  </div>
                )}
                {medication.startDate && (
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-warm-gray mt-0.5" />
                    <div>
                      <p className="text-sm text-warm-gray">Started</p>
                      <p className="font-medium text-navy">
                        {format(new Date(medication.startDate), 'MMMM d, yyyy')}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {medication.instructions && (
                <div className="pt-4 border-t border-border-light">
                  <p className="text-sm text-warm-gray mb-2">Instructions</p>
                  <p className="text-navy">{medication.instructions}</p>
                </div>
              )}

              {medication.sideEffects && (
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <p className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Known Side Effects
                  </p>
                  <p className="text-amber-700">{medication.sideEffects}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Dose History */}
          {doseHistory && doseHistory.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-sage-600" />
                  Recent Dose History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {doseHistory.slice(0, 7).map((dose) => (
                    <div
                      key={dose.id}
                      className="flex items-center justify-between py-2 border-b border-border-light last:border-0"
                    >
                      <div className="flex items-center gap-3">
                        <Badge
                          className={cn(
                            'text-xs',
                            adherenceStatusColors[dose.status]
                          )}
                        >
                          {adherenceStatusLabels[dose.status]}
                        </Badge>
                        <span className="text-sm text-navy">
                          {format(new Date(dose.scheduledTime), 'h:mm a')}
                        </span>
                      </div>
                      <span className="text-sm text-warm-gray">
                        {format(new Date(dose.scheduledTime), 'MMM d')}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Adherence Card */}
          {adherenceStats && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-sage-600" />
                  30-Day Adherence
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  <p className="text-4xl font-display font-bold text-navy">
                    {Math.round(adherenceStats.adherenceRate)}%
                  </p>
                  <p className="text-sm text-warm-gray">Adherence Rate</p>
                </div>
                <Progress value={adherenceStats.adherenceRate} className="h-2" />
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-green-600">
                      {adherenceStats.taken}
                    </p>
                    <p className="text-xs text-green-700">Doses Taken</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-red-600">
                      {adherenceStats.missed}
                    </p>
                    <p className="text-xs text-red-700">Doses Missed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stock Card */}
          {medication.currentQuantity !== undefined && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-sage-600" />
                  Stock Level
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  <p className="text-4xl font-display font-bold text-navy">
                    {medication.currentQuantity}
                  </p>
                  <p className="text-sm text-warm-gray">Remaining</p>
                </div>
                {medication.refillQuantity && (
                  <Progress
                    value={
                      (medication.currentQuantity / medication.refillQuantity) * 100
                    }
                    className={cn(
                      'h-2',
                      isLowStock && '[&>div]:bg-amber-500'
                    )}
                  />
                )}
                {medication.refillDate && (
                  <div className="text-center">
                    <p className="text-sm text-warm-gray">Next Refill</p>
                    <p className="font-medium text-navy">
                      {format(new Date(medication.refillDate), 'MMM d, yyyy')}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          {medication.notes && (
            <Card>
              <CardHeader>
                <CardTitle>Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-warm-gray whitespace-pre-wrap">
                  {medication.notes}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Metadata */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2 text-sm text-warm-gray">
                <p>
                  <span className="font-medium">Added:</span>{' '}
                  {format(new Date(medication.createdAt), 'MMM d, yyyy')}
                </p>
                <p>
                  <span className="font-medium">Last updated:</span>{' '}
                  {formatDistanceToNow(new Date(medication.updatedAt), {
                    addSuffix: true,
                  })}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Skeleton
// ============================================================================

const MedicationDetailsSkeleton: React.FC = () => (
  <div className="space-y-6">
    <div className="flex items-start gap-4">
      <Skeleton className="w-12 h-12 rounded-xl" />
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-32" />
      </div>
    </div>
    <div className="flex gap-2">
      <Skeleton className="h-6 w-20" />
      <Skeleton className="h-6 w-32" />
    </div>
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Skeleton className="h-48" />
        <Skeleton className="h-64" />
      </div>
      <div className="space-y-6">
        <Skeleton className="h-48" />
        <Skeleton className="h-32" />
      </div>
    </div>
  </div>
);

export default MedicationDetails;



